import React, { ChangeEvent, memo, useEffect, useRef } from 'react';

import classNames from 'classnames';

import cls from './TextArea.module.css';

interface TextAreaProps extends Pick<React.AriaAttributes, 'aria-describedby'> {
    label?: string;
    inputName: string;
    onValueChange?: (name: string, value: string) => void;
    onChange?: (value: string) => void;
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
    value: string;
    classes?: string;
    labelClassName?: string;
    textareaClassName?: string;
    maxLength?: number;
    placeholder?: string;
    readOnly?: boolean;
    minRows?: number;
    maxRows?: number;
    /** Pixel limit for autoResize; takes precedence over maxRows. */
    maxHeight?: number;
    autoResize?: boolean;
    isDisabled?: boolean;
    isError?: boolean;
    innerRef?: React.Ref<HTMLTextAreaElement>;
}

const setTextareaRef = (
    ref: React.Ref<HTMLTextAreaElement> | undefined,
    node: HTMLTextAreaElement | null
) => {
    if (!ref) {
        return;
    }

    if (typeof ref === 'function') {
        ref(node);
        return;
    }

    ref.current = node;
};

const adjustTextareaHeight = (
    element: HTMLTextAreaElement,
    maxRows: number,
    heightLimit?: number
) => {
    const computedStyle = window.getComputedStyle(element);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 16;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderHeight =
        heightLimit === undefined
            ? 0
            : parseFloat(computedStyle.borderTopWidth) + parseFloat(computedStyle.borderBottomWidth);
    const maxHeight = heightLimit ?? lineHeight * maxRows + paddingTop + paddingBottom;

    element.style.height = 'auto';
    const contentHeight = element.scrollHeight + borderHeight;
    element.style.height = `${Math.min(contentHeight, maxHeight)}px`;
    element.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
};

export const TextArea = memo(
    ({
        label,
        inputName,
        onChange,
        onBlur,
        onKeyDown,
        value,
        classes,
        labelClassName,
        textareaClassName,
        maxLength = 1000,
        placeholder,
        readOnly = false,
        minRows = 1,
        maxRows = 7,
        maxHeight,
        autoResize = false,
        isDisabled = false,
        isError = false,
        onValueChange,
        innerRef = null,
        'aria-describedby': ariaDescribedBy,
    }: TextAreaProps) => {
        const textareaRef = useRef<HTMLTextAreaElement | null>(null);

        useEffect(() => {
            if (!autoResize || !textareaRef.current) {
                return;
            }

            adjustTextareaHeight(textareaRef.current, maxRows, maxHeight);
        }, [autoResize, maxRows, maxHeight, value]);

        const onInput = (event: ChangeEvent<HTMLTextAreaElement>) => {
            if (autoResize) {
                adjustTextareaHeight(event.target, maxRows, maxHeight);
            }

            onChange?.(event.target.value);
            onValueChange?.(event.target.name, event.target.value);
        };

        return (
            <label className={classNames(cls.Label, labelClassName, [classes])}>
                {label}
                <textarea
                    ref={(node) => {
                        textareaRef.current = node;
                        setTextareaRef(innerRef, node);
                    }}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    onInput={onInput}
                    className={classNames(cls.TextArea, textareaClassName, [
                        isDisabled && cls.disabled,
                        isError && cls.error,
                    ])}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    name={inputName}
                    value={value}
                    readOnly={readOnly}
                    rows={autoResize ? minRows : maxRows}
                    disabled={isDisabled}
                    aria-invalid={isError || undefined}
                    aria-describedby={ariaDescribedBy}
                    style={
                        autoResize
                            ? {
                                  resize: 'none',
                              }
                            : undefined
                    }
                />
            </label>
        );
    }
);
